import React, { useRef } from 'react';
import Page from '../components/Page';
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { AppColors } from '../utils/utils';
import { HtmlRenderer } from '../components/HtmlRenderer';

interface Feature {
  iconPath: string;
  title: string;
  description?: string;
  subtitle1?: string;
  subtitle2?: string;
}

const featureCards: Feature[] = [
  {
    iconPath: '/static/home_page/ic_1_after_hour_new.png',
    title: 'After-Hours<br/>Capture',
    subtitle1:
      'Over 56% of dealership traffic happens after hours - and 0% of dealers capture this data today. QR Buddy turns every late-night shopper into a lead.',
    subtitle2:
      '<b>QR Buddy AI instantly captures and stores</b> every after-hours scan, ensuring no shopper is missed.',
  },
  {
    iconPath: '/static/home_page/ic_3_instant_engagement_new.png',
    title: 'Instant<br/>Engagement',
    subtitle1:
      'Responding to a lead within 5 minutes boosts conversions up to 100x.<br/>QR Buddy routes leads instantly to sales reps.',
    subtitle2:
      '<b>QR Buddy AI engages</b> 24/7 - answering questions, guiding to test drives, and bridging the gap until a rep is live.',
  },
  {
    iconPath: '/static/home_page/ic_4_antiquated_business_cards_new.png',
    title: 'Antiquated<br/>Business Cards ',
    subtitle1:
      'Over 90% of business cards end up in the trash within a week.<br/>QR Buddy replaces cards with digital tracking that never gets lost and always follows up.',
    subtitle2:
      '<b>AI Buddy</b> automatically sends digital follow-ups, keeping your dealership top-of-mind long after the first interaction',
  },
  {
    iconPath: '/static/home_page/ic_2_lost_leads_lost_revenue_new.png',
    title: 'Lost Leads=<br/>Lost Revenue',
    subtitle1:
      'Over 46% of potential sales are lost due to missed or delayed follow-ups.<br/>QR Buddy alerts sales reps in real time so no lead slips through the cracks.',
    subtitle2:
      '<b>QR Buddy AI</b> provides nonstop follow-up - sending reminders, answering FAQs, and nurturing leads 24/7 until a rep closes the sale.',
  },
  {
    iconPath: '/static/home_page/ic_6_faster_sales_cycle_new.png',
    title: 'Faster Sales<br/>Cycle',
    subtitle1:
      'The math is simple; more connected customers = more test drives = more vehicle sales.<br/>QR Buddy accelerates every step of the sales journey.',
    subtitle2:
      '<b>AI Insight:</b> QR Buddy collects names, phone numbers, and buying behaviors to fuel higher conversion probabilities.',
  },
  {
    iconPath: '/static/home_page/ic_5_team_visibility_new.png',
    title: 'Team<br/>Visibility',
    subtitle1:
      'With QR Buddy, every scan and lead is tracked in one dashboard.<br/>QR Buddy gives managers full visibility into rep performance and customer engagement.',
    subtitle2:
      '<b>QR Buddy AI</b> analyzes engagement patterns and flags opportunities for coaching, ensuring no rep falls behind.',
  },
];

const Features: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md')); // if screen <= 'ms' for tablet and mobile device

  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    const scrollAmount = 300; // adjust as needed
    const container = scrollRef.current;

    if (!container) return;

    const maxScrollLeft = 0;
    const maxScrollRight = container.scrollWidth - container.clientWidth;

    if (direction === 'left') {
      // Only scroll left if not already at the start
      if (container.scrollLeft > maxScrollLeft) {
        const newScrollLeft = Math.max(
          container.scrollLeft - scrollAmount,
          maxScrollLeft
        );
        container.scrollTo({
          left: newScrollLeft,
          behavior: 'smooth',
        });
      }
    } else if (direction === 'right') {
      // Only scroll right if not already at the end
      if (container.scrollLeft < maxScrollRight) {
        const newScrollLeft = Math.min(
          container.scrollLeft + scrollAmount,
          maxScrollRight
        );
        container.scrollTo({
          left: newScrollLeft,
          behavior: 'smooth',
        });
      }
    }
  };


  const getFeatureCardView = (card: Feature) => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          p: { xs: 1, sm: 2 },
        }}
      >
        <Typography
          variant='h5'
          sx={{
            color: AppColors.whiteColor,
            fontWeight: 600,
            py: 1,
          }}
        >
          <HtmlRenderer htmlString={card.title} />
        </Typography>

        <Paper
          sx={{
            px: { xs: 2, sm: 4 },
            py: 1,
            borderRadius: '16px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'start',
          }}
        >
          <Box
            component={'img'}
            src={card.iconPath}
            alt='Icon'
            height={'auto'}
            width={'100px'}
            mb={2}
          />

          <Typography
            sx={{
              color: AppColors.blackColor,
            }}
          >
            <HtmlRenderer htmlString={card.subtitle1 ?? ''} />
          </Typography>

          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
              mt: 1.5,
            }}
          >
            <Typography
              sx={{
                color: AppColors.subTitleColor,
              }}
            >
              <HtmlRenderer htmlString={card.subtitle2 ?? ''} />
            </Typography>
          </Stack>
        </Paper>
      </Box>
    );
  };

  const getSectionHeaderView = (text: string, isBigText = false) => {
    return (
      <Box
        sx={{
          px: '20px',
          py: '8px',
          background: AppColors.aiGradientTransparent2,
          borderRadius: '20px',
        }}
      >
        <Typography
          fontWeight={500}
          textAlign={'center'}
          sx={{
            background: AppColors.textGradient,
            '-webkit-background-clip': 'text',
            color: 'transparent',
            fontSize: isBigText
              ? 'clamp(1.25rem, 2vw, 2rem)'
              : 'clamp(0.74rem, 1.5vw, 0.875rem)',
          }}
        >
          {text}
        </Typography>
      </Box>
    );
  };

  const getSectionView = (
    idx: number,
    label: string,
    title: string,
    features: string[],
    imagePath: string,
    isBigImage = false
  ) => {
    const isImageOnRight = idx % 2 !== 0;
    return (
      <Grid
        size={{ xs: 12, sm: 5.9 }}
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        p={3}
        sx={{
          background:
            isMobile && !isImageOnRight
              ? AppColors.aiGradientCrossTransparent
              : 'none',
        }}
      >
        <Box
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'space-between'}
          alignItems={'center'}
          height={'100%'}
        >
          {label && getSectionHeaderView(label)}

          <Typography
            component='p'
            sx={{
              fontSize: 'clamp(1.25rem, 2vw, 2rem)',
              fontWeight: 600,
              color: AppColors.textBlackColor,
              // textAlign: isMobile ? 'center' : 'start',
              textAlign: 'center',
              my: 1,
              // fontSize: { xs: '1.3rem', md: '2.125rem' },
              // fontWeight: 'bold',
              // color: AppColors.textBlackColor,
              '& span': {
                background: AppColors.textGradient,
                '-webkit-background-clip': 'text', // Clip the background to the text
                color: 'transparent', // Make the text color transparent to show the gradient
              },
            }}
          >
            {title}
          </Typography>

          <Box
            sx={{
              flexGrow: 1,
            }}
          >
            {getFeatureListView(features)}
          </Box>

          {imagePath &&
            getScreenshotView(idx, imagePath, isImageOnRight, isBigImage)}

          {/* <GradientButton sx={{ mt: 2 }} onClick={() => {}}>
            Learn More
          </GradientButton> */}
        </Box>
      </Grid>
    );
  };

  const getScreenshotView = (
    idx: number,
    imagePath: string,
    isImageOnRight: boolean,
    isBigImage = false
  ) => {
    const bgImageSize = {
      xs: isBigImage ? '100%' : '120%',
      sm: isBigImage ? '100%' : '120%',
      md: isBigImage ? '100%' : '120%',
      lg: isBigImage ? '100%' : '120%',
      xl: isBigImage ? '80%' : '120%',
    };
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          // overflow: 'hidden',
          mt: 1,
          height: (idx === 0 && !isMobile) || isBigImage ? '100%' : 'auto',
        }}
      >
        <Box
          component='img'
          src={
            isImageOnRight
              ? '/static/images/bg_mobile_right.svg'
              : '/static/images/bg_mobile_left.svg'
          }
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            height: bgImageSize,
            width: bgImageSize,
          }}
        />

        <Box
          component='img'
          src={imagePath}
          // width={{ xs: '80%', sm: '80%', md: '80%' }}
          width={isBigImage ? '100%' : 'auto'}
          maxHeight={{
            xs: isBigImage ? '90%' : '40vh',
            sm: isBigImage ? '100%' : '40vh',
            md: isBigImage ? '85%' : '40vh',
            lg: isBigImage ? '80%' : '40vh',
            xl: isBigImage ? '70%' : '40vh',
          }}
          height={'auto'}
          sx={{ position: 'relative', borderRadius: '18px' }}
        />
      </Box>
    );
  };

  const getSmallSectionView = (
    idx: number,
    label: string,
    title: string,
    features: string[],
    titleColor?: string,
    showCard = true
  ) => {
    return (
      <Grid
        size={{ xs: 12, sm: 5.9 }}
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        p={3}
        sx={{
          background: `url('/static/features_page/img_feature_card_background.png')`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <Box
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'space-between'}
          alignItems={'center'}
          height={'100%'}
          sx={{
            bgcolor: showCard ? 'background.paper' : 'transparent',
            boxShadow: showCard ? 2 : 0,
            p: showCard ? 2 : 0,
            borderRadius: showCard ? '24px' : 0,
          }}
        >
          {label && (
            <Typography
              component='p'
              variant='h4'
              sx={{
                // fontSize: 'clamp(1.25rem, 2vw, 2rem)',
                fontWeight: 600,
                color: AppColors.textBlackColor,
                textAlign: 'center',
                my: 1,
                '& span': {
                  background: AppColors.textGradient,
                  '-webkit-background-clip': 'text', // Clip the background to the text
                  color: 'transparent', // Make the text color transparent to show the gradient
                },
              }}
            >
              {label}
            </Typography>
          )}

          <Typography
            component='p'
            sx={{
              fontSize: 'clamp(1.25rem, 2vw, 2rem)',
              fontWeight: 600,
              color: titleColor ?? AppColors.textBlackColor,
              textAlign: 'center',
              my: 1,
              '& span': {
                background: AppColors.textGradient,
                '-webkit-background-clip': 'text', // Clip the background to the text
                color: 'transparent', // Make the text color transparent to show the gradient
              },
            }}
          >
            {title}
          </Typography>

          <Box
            sx={{
              flexGrow: 1,
            }}
          >
            {getFeatureListView(features)}
          </Box>

          {/* <GradientButton sx={{ mt: 2 }} onClick={() => {}}>
            Learn More
          </GradientButton> */}
        </Box>
      </Grid>
    );
  };

  const getFeatureListView = (list: string[], titleTextColor?: string) => {
    return (
      <Box
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'start'}
      >
        {list.map((item, idx) => {
          return (
            <Stack
              display={'flex'}
              direction={'row'}
              alignItems={'start'}
              alignContent={'start'}
              // px={4}
              py={1}
            >
              <Box
                component={'img'}
                src={'/static/images/ic_ai_arrow.svg'}
                height={'24px'}
                width={'24px'}
              />
              <Typography
                // variant='h6'
                sx={{
                  pl: 1,
                  // fontWeight: '500',
                  color: titleTextColor ?? AppColors.grayThreeColor,
                  // textAlign: 'center',
                  // fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
                  fontSize: 'clamp(0.8rem, 1.2vw, 1rem)',
                  '& span': {
                    background: AppColors.aiGradient,
                    '-webkit-background-clip': 'text', // Clip the background to the text
                    color: 'transparent', // Make the text color transparent to show the gradient
                    fontWeight: 'bold',
                  },
                }}
              >
                <HtmlRenderer htmlString={item} />
              </Typography>
            </Stack>
          );
        })}
      </Box>
    );
  };

  return (
    <>
      <Page title={'Features'}>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            // maxHeight: isPlayIntroVideo ? '100%' : 'auto',
            background: `url('/static/features_page/img_features_banner_background.png')`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            px: '5%',
            py: '2%',
          }}
        >
          {/* <Box
            component='img'
            src='/static/images/ai_gradient_left.svg'
            sx={{
              position: 'absolute',
              left: { xs: -30, md: 0 },
              top: { xs: 0, md: -170 },
              height: { xs: '40%', sm: '30%', md: 'auto' },
              width: { xs: '40%', sm: '30%', md: 'auto' },
            }}
          />

          <Box
            component='img'
            src='/static/images/ai_gradient_right.svg'
            sx={{
              position: 'absolute',
              height: { xs: '60%', sm: '30%', md: 'auto' },
              width: { xs: '60%', sm: '30%', md: 'auto' },
              right: 0,
              bottom: { xs: 0, md: -150 },
            }}
          /> */}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              p: { xs: 3, md: 10 },
              background: `url('/static/features_page/img_features_banner.png')`,
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              borderRadius: { xs: '10px', sm: '20px', md: '25px' },
              gap: '10%',
              position: 'relative',
              overflow: 'clip',
            }}
          >
            <Box
              sx={{
                backgroundColor: '#00000080',

                width: '100%',
                height: '100%',
                position: 'absolute',
              }}
            />

            <Box
              component={'img'}
              src='/static/images/qr_buddy_logo_qr.png'
              sx={{
                height: { xs: '70px', sm: '80px', md: '120px' },
                zIndex: 1,
              }}
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1,
              }}
            >
              <Box
                component='img'
                src='/static/images/qr_buddy_text_logo.png'
                alt='QR Buddy'
                sx={{
                  height: { xs: '5vh', sm: '8vh' },
                }}
              />

              <Typography
                sx={{
                  fontSize: { xs: '1.3rem', md: '2.125rem' },
                  fontWeight: 'bold',
                  color: 'white',
                  mt: 0.5,
                }}
              >
                Features
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            // background: `url('/static/images/img_tile_background.png')`,
            background: AppColors.aiGradient,
            px: { xs: 0, sm: '10%' },
            position: 'relative',
          }}
        >
          <Box
            component={'img'}
            src='/static/images/img_ai_robot_trimmed.png'
            sx={{
              height: { xs: '100px', ms: '150px' },
              position: 'absolute',
              top: { xs: '-5%', sm: '-5%' },
              right: 0,
            }}
          />

          {isMobileOrTablet ? (
            <Box
              sx={{
                maxWidth: '100vw',
                overflow: 'hidden',
                mt: 1,
                position: 'relative',
              }}
            >
              {/* Left Arrow */}
              <IconButton
                onClick={() => scroll('left')}
                // disableRipple={true}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: 5,
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                  backgroundColor: 'rgba(0,0,0,0.4)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.4)',
                  },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>

              <Stack
                ref={scrollRef}
                flexDirection={'row'}
                sx={{
                  overflowX: 'auto',
                  scrollBehavior: 'smooth',
                  px: 4, // padding so arrows don’t overlap cards
                }}
              >
                {featureCards.map((card, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      maxWidth: { xs: '85%', sm: '60%' }, // Make each card take 80% of viewport width
                      flexShrink: 0, // Prevent shrinking
                      flexGrow: 1,
                    }}
                  >
                    {getFeatureCardView(card)}
                  </Box>
                ))}
              </Stack>

              {/* Right Arrow */}
              <IconButton
                onClick={() => scroll('right')}
                // disableRipple={true}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: 5,
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                  backgroundColor: 'rgba(0,0,0,0.4)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.4)',
                  },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          ) : (
            <Grid container justifyContent={'center'}>
              {featureCards.map((card) => {
                return (
                  <Grid
                    size={{ xs: 12, sm: 6, md: 4 }}
                    key={card.title}
                    display={'flex'}
                  >
                    {getFeatureCardView(card)}
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            p: 4,
          }}
        >
          {getSectionHeaderView('QR Buddy — Core Features Overview', true)}
        </Box>

        <Grid
          container
          sx={{
            background: !isMobile
              ? AppColors.aiGradientCrossTransparent
              : 'none',
          }}
        >
          {getSectionView(
            0,
            '',
            'Universal QR Scanner',
            [
              'Reads and interprets any QR code from vehicle window stickers or manufacturer labels.',
              'Instantly identifies the dealership and activates QR Buddy tracking and lead capture.',
            ],
            '/static/features_page/img_qr_scanner.png'
          )}
          {getSectionView(
            1,
            '',
            'AI After-Hours Follow-Up',
            [
              'Engages shoppers who scan vehicles after business hours.',
              'Sends intelligent in-app and chat follow-ups based on user behavior and interest, capturing missed leads for dealers.',
            ],
            '/static/features_page/img_qr_buddy_dashboard.png',
            true
          )}
        </Grid>

        {getSmallSectionView(
          0,
          'QR Buddy Generates Custom QR Codes for Every Dealership and Vehicle',
          'Each code is uniquely tied to your inventory, ensuring every scan delivers accurate, vehicle-specific information in real time.',
          [],
          'white',
          false
        )}

        <Grid container>
          {getSectionView(
            2,
            '',
            'Vehicle Detail Pages',
            [
              'Displays clean, mobile-optimized pages for each scanned vehicle with photos, specs, and pricing.',
              'Lets buyers compare models, save favorites, and contact the dealer instantly.',
            ],
            '/static/features_page/img_vehicle_detail_page.png'
          )}
          {isMobile
            ? getSectionView(
                3,
                '',
                'QR Code Generator',
                [
                  'Allows dealers to easily generate unique QR codes for any vehicle in stock.',
                  'Codes can be attached manually or integrated automatically through the dealership’s DMS.',
                  'Simplifies inventory tracking while ensuring every vehicle on the lot is scan-ready for lead capture.',
                ],
                isMobile ? '' : '/static/features_page/img_coming_soon.png'
              )
            : getSectionView(
                4,
                '',
                'Smart AI Assistant (Miles)',
                [
                  'Conversational AI that guides shoppers through their buying journey — test drives, trade-ins, and vehicle recommendations.',
                  'Operates 24/7 as the “third-shift salesperson,” engaging prospects when staff are unavailable.',
                ],
                // '/static/home_page/img_phone_banner.png'
                '/static/features_page/img_ai_assistant.png'
              )}
        </Grid>

        {/* <SectionViewForFeatures
          idx={1}
          label='See It All, Live'
          title='Owners Dashboard'
          subtitle=''
          features={[
            'Real-time view of all activity across your sales team',
            'Track how many Smart Links are shared and how many new prospects are added',
            'Managers get instant insight into performance, follow-ups, and results',
          ]}
          footerText=''
          imagePath={
            isMobile
              ? '/static/features_page/img_owner_dashboard_new.png'
              : // : '/static/features_page/img_5_owners_dashboard.png'
                '/static/features_page/img_owner_dashboard_new_cropped.png'
          }
          imageBGPath='/static/images/bg_owner_dashboard.svg'
          imageAlign={isMobile ? 'center' : 'end'}
          isForFeature={true}
        /> */}

        <Grid
          container
          sx={{
            background: !isMobile
              ? AppColors.aiGradientCrossTransparent
              : 'none',
          }}
        >
          {isMobile
            ? getSectionView(
                4,
                '',
                'Smart AI Assistant (Miles)',
                [
                  'Conversational AI that guides shoppers through their buying journey — test drives, trade-ins, and vehicle recommendations.',
                  'Operates 24/7 as the “third-shift salesperson,” engaging prospects when staff are unavailable.',
                ],
                // '/static/home_page/img_phone_banner.png'
                '/static/features_page/img_ai_assistant.png'
              )
            : getSectionView(
                3,
                '',
                'QR Code Generator',
                [
                  'Allows dealers to easily generate unique QR codes for any vehicle in stock.',
                  'Codes can be attached manually or integrated automatically through the dealership’s DMS.',
                  'Simplifies inventory tracking while ensuring every vehicle on the lot is scan-ready for lead capture.',
                ],
                '' // '/static/features_page/img_coming_soon.png'
              )}
          {getSectionView(
            5,
            '',
            'Scan History & Comparison',
            [
              'Stores every vehicle a shopper scans for ongoing reference.',
              'Enables side-by-side comparisons and favorites to simplify decision-making.',
            ],
            '' // '/static/features_page/img_coming_soon.png'
          )}
        </Grid>

        {isMobile &&
          getSmallSectionView(7, '', 'Credit Wallet & Lead Purchase System', [
            'Lets dealerships buy and manage lead credits directly within the dashboard.',
            'Wallet automatically deducts per qualified lead or per scan for non-subscribed dealers.',
          ])}

        <Grid container>
          {getSectionView(
            6,
            '',
            'Dealer Dashboard & Lead Routing',
            [
              'Provides real-time visibility into scans, leads, and engagement activity.',
              'Supports round-robin or manual rep assignment to ensure fast follow-up and accountability.',
            ],
            '' // '/static/features_page/img_coming_soon.png'
          )}
          {!isMobile &&
            getSectionView(
              7,
              '',
              'Credit Wallet & Lead Purchase System',
              [
                'Lets dealerships buy and manage lead credits directly within the dashboard.',
                'Wallet automatically deducts per qualified lead or per scan for non-subscribed dealers.',
              ],
              '' // '/static/features_page/img_coming_soon.png'
            )}
        </Grid>

        {isMobile &&
          getSmallSectionView(0, '', 'CRM Integration & Lead Export', [
            'Exports leads seamlessly into DealerSocket, VinSolutions, Salesforce Auto Cloud, and other CRMs.',
            'Includes CSV and API automation options for instant follow-up workflows.',
          ])}

        <Grid
          container
          sx={{
            background: !isMobile
              ? AppColors.aiGradientCrossTransparent
              : 'none',
            display: { xs: 'none', sm: 'flex' },
          }}
        >
          {getSectionView(
            6,
            '',
            'CRM Integration & Lead Export',
            [
              'Exports leads seamlessly into DealerSocket, VinSolutions, Salesforce Auto Cloud, and other CRMs.',
              'Includes CSV and API automation options for instant follow-up workflows.',
            ],
            '' // '/static/features_page/img_coming_soon.png'
          )}
          {getSectionView(
            7,
            '',
            'Behavioral Lead Tracking',
            [
              'Logs every scan, visit, and engagement to build an intelligent behavioral profile of each shopper.',
              'Gives dealers actionable insights into buying intent and interest level.',
            ],
            '' // '/static/features_page/img_coming_soon.png'
          )}
        </Grid>
      </Page>
    </>
  );
};

export default Features;
